import { useState, useEffect } from 'react';


type UseMutlipleSelectReturn<T> = {
	entries: T[];
	setEntries: (entries: T[]) => void;
	resetEntries: () => void;
	filterEntries: (
		fn: (entry: T, index?: number, array?: T[]) => boolean
	) => void;
	selectedEntries: T[];
	setSelectedEntries: (entries: T[]) => void;
	allSelected: boolean;
	isSelected: (entry: T) => boolean;
	toggle: (entry: T) => void;
	toggleAll: (value?: boolean) => boolean;
	select: (entry: T) => boolean;
	unselect: (entry: T) => void;
	selectAll: () => void;
	unselectAll: () => void;
};

type UseMultipleSelectConfig<T> = {
	frozenEntries: T[];
	initialValue: T[];
	onChange?: (values: T[]) => void;
};

export default function useMultipleSelect<T>(
	pool: T[],
	extractUniqueIdentifier: (entry: T) => string | number,
	config: Partial<UseMultipleSelectConfig<T>> = {}
): UseMutlipleSelectReturn<T> {
	const { frozenEntries = [], initialValue = [] } = config;

	const initialSelected = frozenEntries.concat(
		initialValue.filter((x) => !isFrozen(x))
	);
	const [selectedEntries, setSelectedEntriesState] = useState<T[]>(initialSelected);

	useEffect(() => {
		config.onChange?.(selectedEntries);
	}, [selectedEntries]);

	function isFrozen(entry: T | string) {
		const uniqueIdentifier = resolveUniqueIdentifier(entry);
		return frozenEntries
			.map(extractUniqueIdentifier)
			.includes(uniqueIdentifier);
	}

	const [entries, setEntries] = useState<T[]>(pool);

	function resetEntries() {
		setEntries(pool);
	}

	function filterEntries(predicate: (entry: T) => boolean) {
		setEntries(prevEntries => prevEntries.filter(predicate));
	}

	function setSelectedEntries(entriesToSelect: T[]) {
		const ids = entriesToSelect.map(extractUniqueIdentifier);
		setSelectedEntriesState(
			pool.filter((p) => {
				const isToBeSelected = ids.includes(extractUniqueIdentifier(p));
				return isToBeSelected || isFrozen(p);
			})
		);
	}

	const selectedUniqueIdentifiers = selectedEntries.map(extractUniqueIdentifier);

	function resolveUniqueIdentifier(value: string | T) {
		return typeof value === 'string' ? value : extractUniqueIdentifier(value);
	}

	function isSelected(entry: T | string) {
		return (
			isFrozen(entry) ||
			selectedUniqueIdentifiers.includes(resolveUniqueIdentifier(entry))
		);
	}

	function select(entry: T) {
		if (isSelected(entry)) return false;
		setSelectedEntriesState(prev => [...prev, entry]);
		return true;
	}

	function unselect(value: string | T) {
		if (isFrozen(value)) return;
		setSelectedEntriesState(prev => prev.filter(entry => 
			extractUniqueIdentifier(entry) !== resolveUniqueIdentifier(value)
		));
	}

	function toggle(selectedEntry: T) {
        if (isSelected(selectedEntry)) {
            unselect(selectedEntry);
        } else {
            select(selectedEntry);
        }
    }
    
	const allSelected = pool.every((entry) =>
		selectedUniqueIdentifiers.includes(extractUniqueIdentifier(entry))
	);

	function selectAll() {
		setSelectedEntriesState(pool);
	}

	function unselectAll() {
		setSelectedEntriesState(pool.filter(isFrozen));
	}

	function toggleAll(select?: boolean) {
		const shouldSelectAll = select === undefined ? !allSelected : select;
		if (shouldSelectAll) {
			selectAll();
			return true;
		}
		unselectAll();
		return false;
	}

	return {
		entries,
		setEntries,
		resetEntries,
		filterEntries,
		selectedEntries,
		setSelectedEntries,
		allSelected,
		isSelected,
		toggle,
		toggleAll,
		select,
		unselect,
		selectAll,
		unselectAll,
	};
}
